import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import API from "../services/api";

function hasCoords(m) {
  const lat = m.latitude;
  const lon = m.longitude;
  if (lat == null || lat === "" || lon == null || lon === "") return false;
  const la = parseFloat(lat);
  const lo = parseFloat(lon);
  return !Number.isNaN(la) && !Number.isNaN(lo);
}

function createBlueIcon(pulseToken) {
  const pop = pulseToken > 0 ? " map-blue-marker-dot--pop" : "";
  return L.divIcon({
    className: "map-blue-marker-wrap",
    html: `<div class="map-blue-marker-dot${pop}"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -12]
  });
}

function formatCLP(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0
  }).format(value || 0);
}

function BlueMachineMarker({ machine, lat, lng, pulseToken }) {
  const icon = useMemo(
    () => createBlueIcon(pulseToken || 0),
    [pulseToken]
  );

  return (
    <Marker position={[lat, lng]} icon={icon}>
      <Popup>
        <strong>{machine.name}</strong>
        <br />
        {machine.location}
      </Popup>
    </Marker>
  );
}

function MapPage() {
  const [machines, setMachines] = useState([]);
  const [pulseByMachine, setPulseByMachine] = useState({});
  const [saleEvents, setSaleEvents] = useState([]);
  const mapRef = useRef(null);
  const lastSaleIdRef = useRef(0);
  const bootstrappedRef = useRef(false);

  const loadMachines = useCallback(() => {
    API.get("/machines").then(res => setMachines(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  const machinesWithCoords = useMemo(() => machines.filter(hasCoords), [machines]);
  const machinesWithoutCoords = useMemo(() => machines.filter(m => !hasCoords(m)), [machines]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (machinesWithCoords.length === 0) return;

    const map = mapRef.current;
    const bounds = L.latLngBounds(
      machinesWithCoords.map(m => [parseFloat(m.latitude), parseFloat(m.longitude)])
    );
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [machinesWithCoords]);

  useEffect(() => {
    let intervalId;

    const bootstrap = async () => {
      try {
        const res = await API.get("/sales/map-feed/bootstrap");
        const id = Number(res.data?.last_sale_id ?? 0);
        lastSaleIdRef.current = Number.isFinite(id) ? id : 0;
        bootstrappedRef.current = true;
      } catch {
        lastSaleIdRef.current = 0;
        bootstrappedRef.current = true;
      }
    };

    const poll = async () => {
      if (!bootstrappedRef.current) return;
      try {
        const res = await API.get("/sales/map-feed", {
          params: { since_id: lastSaleIdRef.current }
        });
        const sales = res.data?.sales || [];
        for (const s of sales) {
          const sid = Number(s.id);
          if (Number.isFinite(sid)) {
            lastSaleIdRef.current = Math.max(lastSaleIdRef.current, sid);
          }
          const mid = s.machine_id;
          setPulseByMachine(prev => ({
            ...prev,
            [mid]: (prev[mid] || 0) + 1
          }));
          setSaleEvents(prev => {
            const next = [
              {
                key: `${sid}-${Date.now()}`,
                saleId: sid,
                machineName: s.machine_name || `Máquina #${mid}`,
                amount: s.amount,
                at: Date.now()
              },
              ...prev
            ];
            return next.slice(0, 12);
          });
        }
      } catch {
        /* red */
      }
    };

    bootstrap().then(() => {
      poll();
      intervalId = window.setInterval(poll, 1500);
    });

    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Mapa de máquinas</h1>
        <p className="page-subtitle">
          Puntos azules por máquina. Las ventas nuevas animan el punto (~2 s) y aparecen
          abajo como acontecimientos. Solo máquinas con coordenadas; si falta, en{" "}
          <Link to="/machines">Máquinas</Link> usá «Buscar dirección».
        </p>
      </header>

      <div className="card" style={{ overflow: "hidden", padding: 0 }}>
        <MapContainer
          center={[-33.4489, -70.6693]}
          zoom={12}
          whenCreated={mapInstance => {
            mapRef.current = mapInstance;
          }}
          style={{
            height: "min(70vh, 640px)",
            width: "100%"
          }}
        >
          <TileLayer
            attribution="© OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {machinesWithCoords.map(machine => {
            const lat = parseFloat(machine.latitude);
            const lng = parseFloat(machine.longitude);
            const pulseToken = pulseByMachine[machine.id] || 0;
            return (
              <BlueMachineMarker
                key={`${machine.id}-${pulseToken}`}
                machine={machine}
                lat={lat}
                lng={lng}
                pulseToken={pulseToken}
              />
            );
          })}
        </MapContainer>
      </div>

      {saleEvents.length > 0 && (
        <div className="card card--padded" style={{ marginTop: "1rem" }}>
          <h2 className="section-title" style={{ marginBottom: "0.5rem" }}>
            Acontecimientos en el mapa
          </h2>
          <p className="page-subtitle" style={{ marginBottom: "0.65rem" }}>
            Ventas registradas en tiempo casi real desde que abriste esta pantalla.
          </p>
          <div className="map-sale-events">
            {saleEvents.map(ev => (
              <div key={ev.key} className="map-sale-events__item">
                <strong>
                  {formatCLP(ev.amount)} — {ev.machineName}
                </strong>
                <span style={{ color: "var(--color-text-faint)", fontSize: "0.8rem" }}>
                  venta #{ev.saleId}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {machinesWithCoords.length === 0 && (
        <p className="page-subtitle" style={{ marginTop: "1rem" }}>
          No hay máquinas con coordenadas registradas.
        </p>
      )}

      {machinesWithoutCoords.length > 0 && (
        <div className="card card--padded" style={{ marginTop: "1rem" }}>
          <h2 className="section-title" style={{ marginBottom: "0.5rem" }}>
            Sin pin en el mapa ({machinesWithoutCoords.length})
          </h2>
          <p className="page-subtitle" style={{ marginBottom: "0.75rem" }}>
            Estas máquinas aún no tienen latitud/longitud. En{" "}
            <Link to="/machines">Máquinas</Link>, buscá la dirección y elegí un resultado,
            o ingresá lat/lon manualmente.
          </p>
          <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "var(--color-text-muted)" }}>
            {machinesWithoutCoords.map(m => (
              <li key={m.id}>
                <strong>{m.name}</strong>
                {m.location ? ` — ${m.location}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MapPage;
